import { useState, useMemo } from 'react';
import {
  CheckCircle,
  FileText,
  MessageSquare,
  Euro,
  AlertTriangle,
  Clock,
  BarChart3,
  Shield,
  Wrench,
  ChevronDown,
  Download,
} from 'lucide-react';
import type { QuoteLine } from '@/models/savV2Quote';
import { ISavV2 } from '@/models/savV2';
import { savApi } from '../../api/savApi';
import { ISavV2Quote } from '@/models/savV2Quote';
import { DecisionType } from '@/models/savV2Decision';
import { formatDateTime } from '@/utils/dateUtils';
import { formatPrice } from '@/utils/priceHelpers';

interface AnalysisStepProps {
  sav: ISavV2;
  quote: any;
  decision: any;
  completedAt?: string | null;
  previousQuote?: ISavV2Quote | null;
}

const DECISION_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  [DecisionType.ACCORD]: { label: 'Accord total', className: 'tw-bg-green-100 tw-text-green-800' },
  [DecisionType.PARTIAL]: { label: 'Accord partiel', className: 'tw-bg-orange-100 tw-text-orange-800' },
  [DecisionType.REFUSAL]: { label: 'Refus', className: 'tw-bg-red-100 tw-text-red-800' },
};

const COVERAGE_ANSWER: Record<string, string> = {
  yes: 'Oui',
  no: 'Non',
  unknown: 'Indéterminé',
};

const FINANCIAL_RATING: Record<string, { label: string; className: string }> = {
  cheap: { label: 'Bon marché', className: 'tw-text-green-700' },
  normal: { label: 'Normal', className: 'tw-text-gray-700' },
  expensive: { label: 'Élevé', className: 'tw-text-red-700' },
  low: { label: 'Faible', className: 'tw-text-green-700' },
  high: { label: 'Élevé', className: 'tw-text-red-700' },
};

export default function AnalysisStep({ sav, quote, decision, completedAt, previousQuote }: AnalysisStepProps) {
  const [showPrevious, setShowPrevious] = useState(false);
  const activeQuote = showPrevious && previousQuote ? previousQuote : quote;
  const activeDecision = showPrevious && previousQuote ? previousQuote.currentDecision : decision;
  const analysisData = activeQuote?.analysisData;
  const coverage = analysisData?.coverage;
  const financialPoints = analysisData?.financialPoints;
  const negotiationStatus = activeQuote?.negotiationStatus;
  const decisionData = activeDecision?.decisionData;
  const quoteLines = activeQuote?.quoteLines?.lines || [];
  const quoteTotals = activeQuote?.quoteLines?.totals;
  const [showQuoteDetails, setShowQuoteDetails] = useState(false);
  const [showFullDiagnostic, setShowFullDiagnostic] = useState(false);
  // Détection d'analyse périmée (copiée du parent) — par bloc
  const parentQuoteId = activeQuote?.parentQuoteId;
  const isVerificationsInherited = !!(parentQuoteId && analysisData?.analyzedAt && activeQuote?.createdAt
    && Math.abs(new Date(activeQuote.createdAt).getTime() - new Date(analysisData.analyzedAt).getTime()) < 5000);
  const isCoverageInherited = !!(parentQuoteId && previousQuote?.analysisData?.coverage
    && JSON.stringify(coverage) === JSON.stringify(previousQuote.analysisData.coverage));
  const isFinancialsInherited = !!(parentQuoteId && previousQuote?.analysisData?.financialPoints
    && JSON.stringify(financialPoints) === JSON.stringify(previousQuote.analysisData.financialPoints));
  const isNegotiationInherited = !!(parentQuoteId && previousQuote?.negotiationStatus
    && negotiationStatus === previousQuote.negotiationStatus);

  const hasBaseAnalysis = !isVerificationsInherited && !!(analysisData?.diagnostic?.faultCauseComment || analysisData?.diagnostic?.faultCauseLineIds?.length || analysisData?.generalComment);
  const hasCoverage = !isCoverageInherited && !!(coverage && (coverage.faultBeforeContract || coverage.hasExcludedParts !== undefined || coverage.globalComment || (coverage.lineCoverages && Object.keys(coverage.lineCoverages).length > 0)));

  const handleDownloadQuote = async () => {
    if (!activeQuote?.quoteDocumentId) return;
    await savApi.downloadDocument(sav.id, activeQuote.quoteDocumentId, `Devis_${activeQuote.id.slice(0, 8)}`);
  };

  const { partsTotal, laborTotal, otherTotal } = useMemo(() => {
    const calc = (type: string) => quoteLines.reduce((sum: number, l: any) => l.type === type ? sum + (l.quantity * l.unitPrice) : sum, 0);
    return { partsTotal: calc('PART'), laborTotal: calc('LABOR'), otherTotal: calc('OTHER') };
  }, [quoteLines]);

  return (
    <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-shadow-sm tw-overflow-hidden">
      <div className="tw-border-b tw-border-gray-200">
        <h3 className="tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-gray-900 tw-uppercase tw-px-4 tw-py-3 tw-m-0">
          <BarChart3 className="tw-w-4 tw-h-4 tw-text-affinicar-primary" />
          {previousQuote ? (
            <button
              type="button"
              onClick={() => setShowPrevious(false)}
              className={`tw-bg-transparent tw-border-none tw-p-0 tw-cursor-pointer tw-text-base tw-font-semibold tw-uppercase ${!showPrevious ? 'tw-text-gray-900' : 'tw-text-gray-400 hover:tw-text-gray-600'}`}
            >
              Analyse Affinicar
            </button>
          ) : (
            <>Analyse Affinicar</>
          )}
          {completedAt && !showPrevious && (
            <span className="tw-ml-auto tw-text-xs tw-font-normal tw-normal-case tw-text-gray-500">
              Terminée le {new Date(completedAt).toLocaleDateString('fr-FR')} à {new Date(completedAt).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          )}
        </h3>
        {previousQuote && (
          <button
            type="button"
            onClick={() => setShowPrevious(true)}
            className={`tw-flex tw-items-center tw-gap-2 tw-px-4 tw-pb-3 tw-text-xs tw-bg-transparent tw-border-none tw-cursor-pointer ${showPrevious ? 'tw-text-orange-600 tw-font-medium' : 'tw-text-gray-400 hover:tw-text-orange-600'}`}
          >
            <AlertTriangle className="tw-w-3.5 tw-h-3.5" />
            Une première analyse a été effectuée et invalidée
          </button>
        )}
      </div>
      <div className={`tw-p-4 sm:tw-p-6 tw-space-y-5 ${showPrevious ? 'tw-bg-orange-50/30' : ''}`}>

        {/* 1. Synthèse du devis */}
        {activeQuote && quoteLines.length === 0 && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Synthèse du devis</span>
            </div>
            <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
              <FileText className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Devis en cours d'analyse</p>
            </div>
          </div>
        )}
        {activeQuote && quoteLines.length > 0 && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
              <FileText className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Synthèse du devis</span>
              {activeQuote.createdAt && (
                <span className="tw-text-xs tw-text-gray-400">— reçu le {formatDateTime(activeQuote.createdAt)}</span>
              )}
              {activeQuote.quoteDocumentId && (
                <button
                  type="button"
                  onClick={handleDownloadQuote}
                  className="tw-ml-auto tw-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-xs tw-text-affinicar-primary hover:tw-bg-affinicar-primary/10 tw-rounded tw-transition-colors tw-bg-transparent tw-border-none tw-cursor-pointer tw-font-medium"
                >
                  <Download className="tw-w-3.5 tw-h-3.5" />
                  Télécharger le devis
                </button>
              )}
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
              <div className="tw-space-y-1.5 tw-text-sm">
                {partsTotal > 0 && (
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-gray-600">Pièces</span>
                    <span className="tw-font-medium tw-text-gray-900">{formatPrice(partsTotal)}</span>
                  </div>
                )}
                {laborTotal > 0 && (
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-gray-600">Main d'œuvre</span>
                    <span className="tw-font-medium tw-text-gray-900">{formatPrice(laborTotal)}</span>
                  </div>
                )}
                {otherTotal > 0 && (
                  <div className="tw-flex tw-justify-between">
                    <span className="tw-text-gray-600">Autres</span>
                    <span className="tw-font-medium tw-text-gray-900">{formatPrice(otherTotal)}</span>
                  </div>
                )}
                {quoteTotals && (
                  <>
                    <div className="tw-border-t tw-border-gray-200 tw-pt-1.5 tw-mt-1.5" />
                    <div className="tw-flex tw-justify-between">
                      <span className="tw-text-gray-600">Total HT</span>
                      <span className="tw-font-medium tw-text-gray-900">{formatPrice(quoteTotals.totalHt)}</span>
                    </div>
                    <div className="tw-flex tw-justify-between">
                      <span className="tw-text-gray-600">TVA</span>
                      <span className="tw-font-medium tw-text-gray-900">{formatPrice(quoteTotals.totalVat)}</span>
                    </div>
                    <div className="tw-border-t tw-border-gray-200 tw-pt-1.5 tw-mt-1.5" />
                    <div className="tw-flex tw-justify-between">
                      <span className="tw-font-bold tw-text-gray-900">Total TTC</span>
                      <span className="tw-font-bold tw-text-gray-900">{formatPrice(quoteTotals.totalTtc)}</span>
                    </div>
                  </>
                )}
              </div>
              {quoteLines.length > 0 && (
                <>
                  <button
                    type="button"
                    onClick={() => setShowQuoteDetails(!showQuoteDetails)}
                    className="tw-w-full tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200 tw-flex tw-items-center tw-justify-center tw-gap-1 tw-text-xs tw-text-gray-500 hover:tw-text-affinicar-primary tw-bg-transparent tw-border-x-0 tw-border-b-0 tw-cursor-pointer"
                  >
                    <ChevronDown className={`tw-w-3.5 tw-h-3.5 tw-transition-transform ${showQuoteDetails ? 'tw-rotate-180' : ''}`} />
                    {showQuoteDetails ? 'Masquer le détail des lignes' : 'Voir le détail des lignes'}
                  </button>
                  {showQuoteDetails && (
                    <table className="tw-w-full tw-mt-3 tw-text-xs">
                      <thead>
                        <tr className="tw-border-b tw-border-gray-200">
                          <th className="tw-text-left tw-py-1.5 tw-font-medium tw-text-gray-500">Description</th>
                          <th className="tw-text-right tw-py-1.5 tw-font-medium tw-text-gray-500 tw-w-16">Qté</th>
                          <th className="tw-text-right tw-py-1.5 tw-font-medium tw-text-gray-500 tw-w-24">P.U. HT</th>
                          <th className="tw-text-right tw-py-1.5 tw-font-medium tw-text-gray-500 tw-w-24">Total HT</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(['PART', 'LABOR', 'OTHER'] as const).map(type => {
                          const lines = quoteLines.filter((l: QuoteLine) => l.type === type);
                          if (lines.length === 0) return null;
                          const label = type === 'PART' ? 'Pièces' : type === 'LABOR' ? "Main d'œuvre" : 'Autres';
                          return [
                            <tr key={`header-${type}`}>
                              <td colSpan={4} className="tw-pt-2 tw-pb-1 tw-font-semibold tw-text-gray-700 tw-uppercase">{label}</td>
                            </tr>,
                            ...lines.map((line: QuoteLine, i: number) => (
                              <tr key={`${type}-${i}`} className="tw-border-b tw-border-gray-100">
                                <td className="tw-py-1 tw-text-gray-700">{line.description}</td>
                                <td className="tw-py-1 tw-text-right tw-text-gray-500">{line.quantity}</td>
                                <td className="tw-py-1 tw-text-right tw-text-gray-500">{formatPrice(line.unitPrice)}</td>
                                <td className="tw-py-1 tw-text-right tw-font-medium tw-text-gray-900">{formatPrice(line.quantity * line.unitPrice)}</td>
                              </tr>
                            )),
                            <tr key={`total-${type}`} className="tw-border-b tw-border-gray-200">
                              <td colSpan={3} className="tw-py-1 tw-text-right tw-font-semibold tw-text-gray-700">Sous-total {label}</td>
                              <td className="tw-py-1 tw-text-right tw-font-semibold tw-text-gray-900">{formatPrice(lines.reduce((sum: number, l: QuoteLine) => sum + l.quantity * l.unitPrice, 0))}</td>
                            </tr>,
                          ];
                        })}
                      </tbody>
                    </table>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {quoteLines.length > 0 && (<>
        {/* Vérifications et contrôles réalisés */}
        {analysisData && !isVerificationsInherited ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <CheckCircle className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Vérifications et contrôles réalisés</span>
            </div>
            <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-4">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                {[
                  'Validité de la période de garantie',
                  'Contrôle du délai de déclaration',
                  'Contrôle de la carte grise',
                  'Contrôle du kilométrage véhicule',
                  'Contrôle du plafond financier',
                  'Contrôle du plafond kilométrique',
                  'Identification de l\'origine de la panne',
                  'Contrôle de la pièce à l\'origine de la panne en regard du contrat',
                  ...(activeDecision?.decisionType === DecisionType.ACCORD || activeDecision?.decisionType === DecisionType.PARTIAL
                    ? ['Analyse coût pièce et main d\'œuvre', 'Négociation du devis']
                    : []),
                ].map((label) => (
                  <div key={label} className="tw-flex tw-items-center tw-gap-2">
                    <CheckCircle className="tw-w-4 tw-h-4 tw-text-green-600 tw-flex-shrink-0" />
                    <span className="tw-text-sm tw-text-green-800">{label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : activeQuote && quoteLines.length > 0 ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <CheckCircle className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Vérifications et contrôles</span>
            </div>
            <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
              <CheckCircle className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">En attente des vérifications et contrôles</p>
            </div>
          </div>
        ) : null}

        {/* Message quand l'analyse n'est plus disponible */}
        {showPrevious && !analysisData && (
          <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
            <AlertTriangle className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
            <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">L'analyse de ce devis n'est plus disponible</p>
          </div>
        )}

        {/* 2. Analyse de base datée */}
        {analysisData && !isVerificationsInherited && (analysisData.diagnostic?.faultCauseComment || analysisData.diagnostic?.faultCauseLineIds?.length > 0 || analysisData.generalComment) ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <CheckCircle className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Analyse de base</span>
              {analysisData.analyzedAt && (
                <span className="tw-text-xs tw-text-gray-400 tw-ml-1">— {formatDateTime(analysisData.analyzedAt)}</span>
              )}
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-3">
              {analysisData.diagnostic?.faultCauseComment && (() => {
                const fullText = analysisData.diagnostic.faultCauseComment;
                const paragraphs = fullText.split(/\n\n/);
                const hasMore = paragraphs.length > 1;
                return (
                  <div>
                    <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">Diagnostic</span>
                    <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5 tw-whitespace-pre-line">
                      {showFullDiagnostic ? fullText : paragraphs[0]}
                    </p>
                    {hasMore && (
                      <button
                        type="button"
                        onClick={() => setShowFullDiagnostic(!showFullDiagnostic)}
                        className="tw-mt-1 tw-text-xs tw-text-affinicar-primary hover:tw-underline tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-0"
                      >
                        {showFullDiagnostic ? 'Voir moins' : 'Voir plus'}
                      </button>
                    )}
                  </div>
                );
              })()}
              {analysisData.diagnostic?.faultCauseLineIds?.length > 0 && (
                <div>
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">Lignes identifiées comme cause</span>
                  <div className="tw-flex tw-flex-wrap tw-gap-1 tw-mt-1">
                    {analysisData.diagnostic.faultCauseLineIds.map((lineId: string) => {
                      const index = lineId.startsWith('line-') ? parseInt(lineId.split('-')[1]) : parseInt(lineId);
                      const line = !isNaN(index) ? quoteLines[index] : quoteLines.find((_: any) => _?.id === lineId);
                      return (
                        <span key={lineId} className="tw-px-2 tw-py-0.5 tw-bg-orange-100 tw-text-orange-800 tw-rounded-full tw-text-xs tw-font-medium">
                          {line?.description || `Ligne ${lineId}`}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              {analysisData.generalComment && (
                <div>
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase">Commentaire général</span>
                  <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5 tw-italic">"{analysisData.generalComment}"</p>
                </div>
              )}
            </div>
          </div>
        ) : analysisData && !isVerificationsInherited && quoteLines.length > 0 ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <CheckCircle className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Analyse de base</span>
            </div>
            <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
              <BarChart3 className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Analyse de base en cours</p>
            </div>
          </div>
        ) : null}

        {/* 3. Analyse financière */}
        {!isFinancialsInherited && financialPoints && !financialPoints.skipped && (financialPoints.partPrice || financialPoints.laborRate || financialPoints.laborHours) && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Euro className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Analyse financière</span>
            </div>
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-3 tw-gap-3">
              {financialPoints.partPrice && (
                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                  <span className="tw-text-xs tw-text-gray-500">Prix des pièces</span>
                  <p className={`tw-text-sm tw-font-medium tw-mb-0 ${FINANCIAL_RATING[financialPoints.partPrice.rating]?.className || 'tw-text-gray-700'}`}>
                    {FINANCIAL_RATING[financialPoints.partPrice.rating]?.label || financialPoints.partPrice.rating}
                  </p>
                  {financialPoints.partPrice.comment && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0 tw-mt-0.5">{financialPoints.partPrice.comment}</p>
                  )}
                </div>
              )}
              {financialPoints.laborRate && (
                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                  <span className="tw-text-xs tw-text-gray-500">Taux horaire</span>
                  <p className={`tw-text-sm tw-font-medium tw-mb-0 ${FINANCIAL_RATING[financialPoints.laborRate.rating]?.className || 'tw-text-gray-700'}`}>
                    {FINANCIAL_RATING[financialPoints.laborRate.rating]?.label || financialPoints.laborRate.rating}
                  </p>
                  {financialPoints.laborRate.comment && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0 tw-mt-0.5">{financialPoints.laborRate.comment}</p>
                  )}
                </div>
              )}
              {financialPoints.laborHours && (
                <div className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
                  <span className="tw-text-xs tw-text-gray-500">Heures de main d'œuvre</span>
                  <p className={`tw-text-sm tw-font-medium tw-mb-0 ${FINANCIAL_RATING[financialPoints.laborHours.rating]?.className || 'tw-text-gray-700'}`}>
                    {FINANCIAL_RATING[financialPoints.laborHours.rating]?.label || financialPoints.laborHours.rating}
                  </p>
                  {financialPoints.laborHours.comment && (
                    <p className="tw-text-xs tw-text-gray-500 tw-mb-0 tw-mt-0.5">{financialPoints.laborHours.comment}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 5. Analyse vs contrat (couverture) */}
        {hasCoverage ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Shield className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Analyse vs contrat</span>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-3">
              {/* Questions de couverture */}
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-2">
                {coverage.faultBeforeContract && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-sm tw-text-gray-600">Panne antérieure au contrat :</span>
                    <span className={`tw-text-sm tw-font-medium ${coverage.faultBeforeContract === 'yes' ? 'tw-text-red-700' : coverage.faultBeforeContract === 'no' ? 'tw-text-green-700' : 'tw-text-gray-500'}`}>
                      {COVERAGE_ANSWER[coverage.faultBeforeContract]}
                    </span>
                  </div>
                )}
                {coverage.hasExcludedParts !== undefined && (
                  <div className="tw-flex tw-items-center tw-gap-2">
                    <span className="tw-text-sm tw-text-gray-600">Pièces exclues :</span>
                    <span className={`tw-text-sm tw-font-medium ${coverage.hasExcludedParts ? 'tw-text-red-700' : 'tw-text-green-700'}`}>
                      {coverage.hasExcludedParts ? 'Oui' : 'Non'}
                    </span>
                  </div>
                )}
              </div>

              {coverage.globalComment && (
                <p className="tw-text-sm tw-text-gray-600 tw-italic tw-mb-0">"{coverage.globalComment}"</p>
              )}

              {/* Couverture par ligne */}
              {coverage.lineCoverages && Object.keys(coverage.lineCoverages).length > 0 && (
                <div>
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-block tw-mb-1.5">Couverture par ligne</span>
                  <div className="tw-space-y-1.5">
                    {Object.entries(coverage.lineCoverages).map(([lineId, lc]: [string, any]) => {
                      const index = lineId.startsWith('line-') ? parseInt(lineId.split('-')[1]) : parseInt(lineId);
                      const line = !isNaN(index) ? quoteLines[index] : quoteLines.find((_: any) => _?.id === lineId);
                      return (
                        <div key={lineId} className="tw-flex tw-items-center tw-gap-2 tw-text-sm">
                          <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-min-w-[48px] tw-justify-center ${
                            lc.coveragePercent === 100 ? 'tw-bg-green-100 tw-text-green-800'
                            : lc.coveragePercent === 0 ? 'tw-bg-red-100 tw-text-red-800'
                            : 'tw-bg-orange-100 tw-text-orange-800'
                          }`}>
                            {lc.coveragePercent}%
                          </span>
                          <span className="tw-text-gray-700 tw-truncate">{line?.description || `Ligne ${lineId}`}</span>
                          {lc.reason && <span className="tw-text-gray-500 tw-text-xs">({lc.reason})</span>}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : hasBaseAnalysis ? (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Shield className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Analyse vs contrat</span>
            </div>
            <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
              <Shield className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
              <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Analyse vs contrat en cours</p>
            </div>
          </div>
        ) : null}

        {/* 6. Devis comparatif */}
        {!isCoverageInherited && activeQuote?.parentQuoteId && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <MessageSquare className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Devis comparatif</span>
            </div>
            <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-4">
              <p className="tw-text-sm tw-text-blue-800 tw-mb-0">
                Ce devis est un devis négocié. Il remplace le devis original.
              </p>
            </div>
          </div>
        )}

        {/* Challenge technique & négociation */}
        {!isNegotiationInherited && negotiationStatus && negotiationStatus !== 'not_needed' && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Wrench className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Challenge technique & négociation</span>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4 tw-space-y-2">
              <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                Prise de contact avec le réparateur pour challenger le devis d'un point de vue technique
              </p>
              {activeQuote?.negotiationStartedAt && (
                <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
                  <span className="tw-font-medium">Début de la négociation :</span> {formatDateTime(activeQuote.negotiationStartedAt)}
                </p>
              )}
              {activeQuote?.negotiationNotes && (
                <div className="tw-mt-2">
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500">Notes de négociation</span>
                  <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5 tw-italic tw-whitespace-pre-line">"{activeQuote.negotiationNotes}"</p>
                </div>
              )}
              {activeQuote?.negotiationCancelledReason && (
                <div className="tw-mt-2 tw-bg-red-50 tw-rounded tw-p-2">
                  <span className="tw-text-xs tw-font-medium tw-text-red-600">Négociation annulée</span>
                  <p className="tw-text-sm tw-text-red-700 tw-mb-0 tw-mt-0.5">{activeQuote.negotiationCancelledReason}</p>
                </div>
              )}
            </div>
            {!showPrevious && negotiationStatus === 'negotiating' && (
              <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center tw-mt-3">
                <Wrench className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
                <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Négociation en cours</p>
              </div>
            )}
            {showPrevious && (
              <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center tw-mt-3">
                <Wrench className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
                <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">Ce devis a été invalidé suite à la négociation. Un nouveau devis a été demandé.</p>
              </div>
            )}
          </div>
        )}

        {/* 7. Formalisation de la décision */}
        {!showPrevious && ((activeDecision && activeDecision.status === 'DRAFT') || (!activeDecision && hasCoverage && !isNegotiationInherited && negotiationStatus !== 'negotiating')) && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <Clock className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Formalisation de la décision</span>
            </div>
            <div className="tw-bg-amber-50 tw-border tw-border-amber-200 tw-rounded-lg tw-p-4">
              <p className="tw-text-sm tw-text-amber-800 tw-mb-0">Recommandation en cours de formalisation</p>
            </div>
          </div>
        )}

        {/* 8. Recommandation */}
        {!isCoverageInherited && activeDecision && activeDecision.status !== 'DRAFT' && (
          <div>
            <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2">
              <AlertTriangle className="tw-w-4 tw-h-4 tw-text-gray-400" />
              <span className="tw-text-xs tw-text-gray-500 tw-uppercase tw-font-medium">Recommandation</span>
              <span className={`tw-inline-flex tw-items-center tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium ${DECISION_TYPE_CONFIG[activeDecision.decisionType]?.className || 'tw-bg-gray-100 tw-text-gray-700'}`}>
                {DECISION_TYPE_CONFIG[activeDecision.decisionType]?.label || activeDecision.decisionType}
              </span>
            </div>
            <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
              <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                {decisionData?.totalAmount !== undefined && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Total devis</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-gray-900 tw-mb-0">{formatPrice(decisionData.totalAmount)}</p>
                  </div>
                )}
                {decisionData?.coveredAmount !== undefined && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Couvert</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-green-700 tw-mb-0">{formatPrice(decisionData.coveredAmount)}</p>
                  </div>
                )}
                {decisionData?.uncoveredAmount !== undefined && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Non couvert</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-red-700 tw-mb-0">{formatPrice(decisionData.uncoveredAmount)}</p>
                  </div>
                )}
                {decisionData?.totalCommercialAmount > 0 && (
                  <div>
                    <span className="tw-text-xs tw-text-gray-500">Geste commercial</span>
                    <p className="tw-text-sm tw-font-semibold tw-text-blue-700 tw-mb-0">{formatPrice(decisionData.totalCommercialAmount)}</p>
                  </div>
                )}
              </div>
              {decisionData?.explanationGarant && (
                <div className="tw-mt-3 tw-pt-3 tw-border-t tw-border-gray-200">
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500">Explication garant</span>
                  <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5">{decisionData.explanationGarant}</p>
                </div>
              )}
              {decisionData?.explanationBeneficiary && (
                <div className="tw-mt-2">
                  <span className="tw-text-xs tw-font-medium tw-text-gray-500">Explication bénéficiaire</span>
                  <p className="tw-text-sm tw-text-gray-700 tw-mb-0 tw-mt-0.5">{decisionData.explanationBeneficiary}</p>
                </div>
              )}
              {decisionData?.refusalReason && (
                <div className="tw-mt-2 tw-bg-red-50 tw-rounded tw-p-2">
                  <span className="tw-text-xs tw-font-medium tw-text-red-600">Motif de refus</span>
                  <p className="tw-text-sm tw-text-red-700 tw-mb-0 tw-mt-0.5">{decisionData.refusalReason}</p>
                </div>
              )}
            </div>
          </div>
        )}

        </>)}

        {/* En attente de devis */}
        {!activeQuote && (
          <div className="tw-bg-orange-50 tw-border tw-border-orange-200 tw-rounded-lg tw-p-4 tw-text-center">
            <FileText className="tw-w-6 tw-h-6 tw-text-orange-500 tw-mx-auto tw-mb-2" />
            <p className="tw-text-sm tw-font-medium tw-text-orange-800 tw-mb-0">En attente de réception du devis</p>
          </div>
        )}

      </div>
    </div>
  );
}

export { AnalysisStep };
